import type { ReactNode } from "react";

import { Card } from "../components/Display";
import { Icon } from "../components/Icon";
import { Logo } from "../components/Logo";
import { cn } from "../lib/cn";

export interface AuthLayoutProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Below the card, e.g. "New here? Create an account". */
  footer?: ReactNode;
  /** Product badge in the logo: "ADMIN", "VENDOR"… */
  product: string;
  /** Brand panel copy (desktop only). */
  headline: ReactNode;
  subheadline?: ReactNode;
  points?: ReactNode[];
  /** Top-right corner, e.g. language switcher. */
  corner?: ReactNode;
}

export interface AuthCardProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Below the card, e.g. "New here? Create an account". */
  footer?: ReactNode;
  className?: string;
}

/**
 * The form card of a login / password screen: heading, optional description, the form, and a line below the card.
 * `AuthLayout` frames it for the panels; the storefront shows it inside its own header and footer.
 */
export function AuthCard({ title, description, children, footer, className }: AuthCardProps) {
  return (
    <div className={cn("w-full max-w-md", className)}>
      <Card className="p-6 sm:p-8">
        <div className="mb-6 space-y-1.5">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {children}
      </Card>
      {footer && <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>}
    </div>
  );
}

/** Login / password screens: ink brand panel with yellow accents (desktop) + form card. */
export function AuthLayout({
  title,
  description,
  children,
  footer,
  product,
  headline,
  subheadline,
  points = [],
  corner,
}: AuthLayoutProps) {
  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <aside className="relative hidden overflow-hidden bg-ink p-12 text-ink-foreground lg:flex lg:flex-col">
        <Logo tone="light" product={product} />
        <div className="my-auto max-w-md space-y-6">
          <span className="block h-1.5 w-16 rounded-full bg-brand" aria-hidden />
          <h2 className="font-display text-4xl leading-tight font-bold">{headline}</h2>
          {subheadline && <p className="text-lg text-ink-foreground/70">{subheadline}</p>}
          {points.length > 0 && (
            <ul className="space-y-3">
              {points.map((point, index) => (
                <li key={index} className="flex items-start gap-3 text-ink-foreground/85">
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
                    <Icon name="check" size={14} strokeWidth={3} />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="text-xs text-ink-foreground/40">© {new Date().getFullYear()} MOTOLYNK</p>
        <div
          className="pointer-events-none absolute -right-24 -bottom-24 size-80 rounded-full bg-brand/15"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -top-16 right-16 size-40 rounded-full bg-brand/10"
          aria-hidden
        />
      </aside>

      <main className="relative flex flex-col items-center justify-center px-4 py-10 sm:px-8">
        {corner && <div className="absolute top-4 right-4">{corner}</div>}
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center lg:hidden">
            <Logo product={product} />
          </div>
          <AuthCard title={title} description={description} footer={footer}>
            {children}
          </AuthCard>
        </div>
      </main>
    </div>
  );
}
